import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import EditableDataTable from '../components/EditableDataTable';

export default function Requests({ showToast, editMode }) {
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ type: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [docsByRequest, setDocsByRequest] = useState({});   // { [requestId]: [docs] }
  const [selected, setSelected] = useState(null);           // request open in detail modal
  const [uploading, setUploading] = useState(false);

  async function attachToRequest(file) {
    if (!file || !selected) return;
    if (file.size > 50 * 1024 * 1024) { showToast('⚠️ File is larger than 50 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('linked_entity_type', 'request');
      fd.append('linked_entity_id', selected.id);
      fd.append('category', 'request_attachment');
      fd.append('folder_path', `Legal/Requests/${selected.ref}`);
      await api.uploadDocument(fd);
      showToast(`✅ ${file.name} attached to ${selected.ref}`);
      await loadAttachments();
    } catch (err) {
      showToast('⚠️ Upload FAILED: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  }

  useEffect(() => { loadRequests(); }, [filters]);
  async function loadRequests() {
    try {
      const { data } = await api.getRequests(filters);
      const list = data.data || [];
      setRequests(list);
      loadAttachments();
    }
    catch (err) { showToast('Failed to load requests'); } finally { setLoading(false); }
  }

  // Pull every request-linked document in one call and group by request,
  // so the register can show at a glance which requests carry attachments.
  async function loadAttachments() {
    try {
      const { data } = await api.getDocuments({ linked_entity_type: 'request' });
      const rows = Array.isArray(data) ? data : (data.data || []);
      const grouped = {};
      rows.forEach(d => {
        if (!d.linked_entity_id) return;
        (grouped[d.linked_entity_id] = grouped[d.linked_entity_id] || []).push(d);
      });
      setDocsByRequest(grouped);
    } catch (err) { /* non-fatal — register still renders */ }
  }
  async function updateStatus(id, status) {
    try { await api.updateRequest(id, { status }); showToast('Request updated'); loadRequests(); } catch (err) { showToast('Update failed'); }
  }

  const statusBadge = (s) => { const c={pending:'badge-blue',in_review:'badge-gold',completed:'badge-green',overdue:'badge-red',cancelled:'badge-gray'}; return <span className={`badge ${c[s]||'badge-gray'}`}><span className="dot"></span> {s?.replace(/_/g,' ')}</span>; };
  const priorityBadge = (p) => { const c={low:'badge-gray',medium:'badge-gold',high:'badge-red',urgent:'badge-red'}; return <span className={`badge ${c[p]||'badge-gray'}`}>{p}</span>; };

  const TABLE_COLUMNS = [
    { key:'ref', label:'Ref' }, { key:'subject', label:'Subject' }, { key:'type', label:'Type' },
    { key:'requester_name', label:'Requester' }, { key:'requester_dept', label:'Department' },
    { key:'priority', label:'Priority' }, { key:'status', label:'Status' },
    { key:'attachments', label:'Docs' },
  ];

  const attachmentsFor = (id) => docsByRequest[id] || [];

  const renderCell = (row, colKey) => {
    if (colKey === 'ref') return <span onClick={()=>setSelected(row)} style={{fontWeight:700,color:'var(--mid)',fontSize:11,cursor:'pointer',textDecoration:'underline dotted',textUnderlineOffset:3}}>{row.ref}</span>;
    if (colKey === 'attachments') {
      const docs = attachmentsFor(row.id);
      if (!docs.length) return <span style={{fontSize:11,color:'var(--muted)'}}>—</span>;
      return (
        <span onClick={()=>setSelected(row)} title={docs.map(d=>d.name).join(', ')}
          style={{cursor:'pointer',fontSize:11,fontWeight:600,color:'#16A34A',background:'#ECFDF5',border:'1px solid #A7F3D0',borderRadius:12,padding:'2px 8px',whiteSpace:'nowrap'}}>
          📎 {docs.length}
        </span>
      );
    }
    if (colKey === 'type') return row.type?.replace(/_/g,' ');
    if (colKey === 'priority') return priorityBadge(row.priority);
    if (colKey === 'status') return statusBadge(row.status);
    if (colKey === 'requester_dept') return row.requester_dept || '-';
    return row[colKey] ?? '';
  };

  return (<div>
    <div className="section-header">
      <div className="section-title">All Service Requests</div>
      <div style={{display:'flex',gap:8}}>
        <select style={{fontSize:12,padding:'6px 10px',borderRadius:8,border:'1px solid var(--border)'}} value={filters.type} onChange={e=>setFilters({...filters,type:e.target.value})}>
          <option value="">All Types</option><option value="contract_draft">Contract Draft</option><option value="nda">NDA</option><option value="document_review">Review</option><option value="legal_opinion">Legal Opinion</option><option value="itt_qualification">ITT</option>
        </select>
        <select style={{fontSize:12,padding:'6px 10px',borderRadius:8,border:'1px solid var(--border)'}} value={filters.status} onChange={e=>setFilters({...filters,status:e.target.value})}>
          <option value="">All Status</option><option value="pending">Pending</option><option value="in_review">In Review</option><option value="completed">Completed</option><option value="overdue">Overdue</option>
        </select>
      </div>
    </div>
    <div className="card">
      {loading ? <div className="loading">Loading...</div> : (
        <EditableDataTable
          columns={TABLE_COLUMNS}
          data={requests}
          editable={editMode}
          onDataChange={(updated) => setRequests(updated)}
          renderCell={renderCell}
        />
      )}
      {/* Action buttons below table when not in edit mode */}
      {!editMode && !loading && requests.length > 0 && (
        <div style={{marginTop:14}}>
          <table><thead><tr><th style={{width:80}}>Ref</th><th>Subject</th><th style={{width:100}}>Action</th></tr></thead>
          <tbody>{requests.filter(r=>r.status!=='completed'&&r.status!=='cancelled').slice(0,10).map(r=>(
            <tr key={r.id+'act'}>
              <td style={{fontWeight:600,color:'var(--mid)',fontSize:11}}>{r.ref}</td><td>{r.subject}</td>
              <td>{r.status==='pending'&&<button className="sig-btn primary" onClick={()=>updateStatus(r.id,'in_review')}>Accept</button>}
                {r.status==='in_review'&&<button className="sig-btn primary" onClick={()=>updateStatus(r.id,'completed')}>Complete</button>}</td>
            </tr>
          ))}</tbody></table>
        </div>
      )}
    </div>

    {/* ── REQUEST DETAIL + ATTACHMENTS ── */}
    {selected && (
      <div onClick={()=>setSelected(null)} style={{position:'fixed',inset:0,background:'rgba(10,20,50,0.72)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(3px)',WebkitBackdropFilter:'blur(3px)'}}>
        <div onClick={e=>e.stopPropagation()} style={{background:'#ffffff',borderRadius:12,padding:28,width:600,maxWidth:'94vw',maxHeight:'85vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.5)',border:'1px solid #e2e8f0'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
            <div>
              <span style={{fontWeight:800,fontSize:13,color:'var(--mid)',letterSpacing:1}}>{selected.ref}</span>
              <div style={{fontWeight:700,fontSize:17,color:'var(--navy)',marginTop:2}}>{selected.subject}</div>
            </div>
            <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--muted)',lineHeight:1}}>✕</button>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 20px',fontSize:13}}>
            {[
              ['Type', selected.type?.replace(/_/g,' ')],
              ['Priority', selected.priority],
              ['Status', selected.status?.replace(/_/g,' ')],
              ['Requester', selected.requester_name || '—'],
              ['Department', selected.requester_dept || '—'],
              ['Date Required', selected.date_required ? new Date(selected.date_required).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'],
            ].map(([label,val]) => (
              <div key={label}>
                <div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:2}}>{label}</div>
                <div style={{fontWeight:500,color:'var(--navy)'}}>{val}</div>
              </div>
            ))}
          </div>

          {selected.description && (
            <div style={{marginTop:14}}>
              <div style={{fontSize:10,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:4}}>Description</div>
              <div style={{fontSize:13,color:'var(--navy)',lineHeight:1.5,whiteSpace:'pre-wrap'}}>{selected.description}</div>
            </div>
          )}

          {/* Attachments — the whole point: no more hunting in SharePoint */}
          <div style={{marginTop:18,borderTop:'1px solid #e2e8f0',paddingTop:14}}>
            <div style={{fontSize:10,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:0.8,marginBottom:8}}>
              Attachments ({attachmentsFor(selected.id).length})
            </div>
            {attachmentsFor(selected.id).length === 0 ? (
              <div style={{fontSize:12,color:'#92400E',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:7,padding:'9px 12px'}}>
                No document is attached to this request. If the requester believes they attached one,
                it did not reach SharePoint — use <strong>Attach Document</strong> below to add it.
              </div>
            ) : attachmentsFor(selected.id).map(d => (
              <div key={d.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',border:'1px solid #e2e8f0',borderRadius:7,marginBottom:6,fontSize:12}}>
                <span style={{fontSize:15}}>📎</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,color:'var(--navy)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</div>
                  <div style={{fontSize:10,color:'var(--muted)',marginTop:1}}>
                    {d.sharepoint_path || '—'}
                    {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}
                    {d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString('en-GB')}` : ''}
                  </div>
                </div>
                {d.sharepoint_url ? (
                  <a href={d.sharepoint_url} target="_blank" rel="noopener noreferrer"
                    style={{background:'#16A34A',color:'#fff',borderRadius:5,padding:'4px 12px',fontSize:11,fontWeight:600,textDecoration:'none',whiteSpace:'nowrap'}}>Open</a>
                ) : (
                  <span title="Upload to SharePoint did not complete" style={{background:'#FEF2F2',color:'#DC2626',border:'1px solid #FECACA',borderRadius:5,padding:'3px 9px',fontSize:10,fontWeight:600,whiteSpace:'nowrap'}}>No SP link</span>
                )}
              </div>
            ))}
          </div>

          <label style={{marginTop:10,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'8px 10px',border:'1px dashed #2563EB',borderRadius:7,background:'#EFF6FF',color:'#1046A0',fontSize:12,fontWeight:600,cursor:uploading?'wait':'pointer',opacity:uploading?0.6:1}}>
            {uploading ? 'Uploading…' : '📎 Attach Document'}
            <input type="file" disabled={uploading} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              style={{display:'none'}} onChange={e => { attachToRequest(e.target.files[0]); e.target.value=''; }} />
          </label>

          <div style={{marginTop:16,display:'flex',gap:10,justifyContent:'flex-end'}}>
            {selected.status === 'pending' && <button className="topbar-btn btn-primary" onClick={()=>{updateStatus(selected.id,'in_review');setSelected(null);}}>Accept</button>}
            {selected.status === 'in_review' && <button className="topbar-btn btn-primary" onClick={()=>{updateStatus(selected.id,'completed');setSelected(null);}}>Mark Complete</button>}
            <button className="topbar-btn btn-ghost" onClick={()=>setSelected(null)}>Close</button>
          </div>
        </div>
      </div>
    )}
  </div>);
}
